import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlass, Trash, FilePdf } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ChequeStatusActions from './ChequeStatusActions';
import { NC, fmtEur, STATUS_LABEL, STATUS_CLS } from './chequesApi';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const emptyForm = { patient_id: '', nutri_id: '', program_edition: '2026', cheque_number: '', registration_date: new Date().toISOString().slice(0, 10), gross: '40', withholding_pct: '25', filipa_pct: '60' };

export default function ChequeDialog({ open, onClose, chequeId, isAdmin, staff, onSaved }) {
  const confirmDelete = useConfirmDelete();
  const [cheque, setCheque] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!chequeId) { setCheque(null); setForm(emptyForm); return; }
    try { const { data } = await axios.get(`${NC}/${chequeId}`); setCheque(data); setForm({ ...data, gross: data.gross, withholding_pct: data.withholding_pct, filipa_pct: data.filipa_pct || 60 }); }
    catch { toast.error('Erro ao carregar cheque'); }
  }, [chequeId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    if (!patientSearch || patientSearch.trim().length < 2) { setPatients([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await axios.get(`${API}/api/patients?search=${encodeURIComponent(patientSearch.trim())}&limit=8`); setPatients(data); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [patientSearch]);

  useEffect(() => {
    const pid = form.patient_id;
    if (!pid) { setConsultations([]); return; }
    axios.get(`${API}/api/patients/${pid}/consultations`).then(({ data }) => setConsultations(data)).catch(() => setConsultations([]));
  }, [form.patient_id]);

  const nutriIsAdmin = useMemo(() => staff.find(s => s.user_id === form.nutri_id)?.role === 'admin', [staff, form.nutri_id]);

  const preview = useMemo(() => {
    const grossC = Math.round(parseFloat(form.gross || 0) * 100);
    const withholdC = Math.round(grossC * parseFloat(form.withholding_pct || 0) / 100);
    const netC = grossC - withholdC;
    const requiresSplit = !nutriIsAdmin && !!form.nutri_id;
    const filipaC = requiresSplit ? Math.floor(grossC * parseFloat(form.filipa_pct || 0) / 100) : 0;
    const anaC = netC - filipaC;
    return { gross: grossC / 100, withhold: withholdC / 100, net: netC / 100, filipa: filipaC / 100, ana: anaC / 100, requiresSplit };
  }, [form, nutriIsAdmin]);

  const save = async () => {
    if (!form.patient_id || !form.nutri_id) { toast.error('Paciente e nutricionista são obrigatórios'); return; }
    setSaving(true);
    try {
      if (chequeId) {
        await axios.put(`${NC}/${chequeId}`, form);
        toast.success('Cheque atualizado');
      } else {
        await axios.post(NC, form);
        toast.success('Cheque criado');
      }
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao guardar'); }
    setSaving(false);
  };

  const linkConsultation = async (consultId) => {
    const c = consultations.find(x => x.id === consultId);
    setForm(f => ({ ...f, consultation_id: consultId, consultation_date: c?.date || f.consultation_date }));
    if (chequeId) {
      try { await axios.put(`${NC}/${chequeId}`, { consultation_id: consultId, consultation_date: c?.date || '' }); load(); onSaved(); toast.success('Consulta associada'); }
      catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    }
  };

  const remove = async () => {
    const linked = [];
    if (cheque?.has_invoice) linked.push(`fatura ${cheque.invoice_filename || ''}`.trim());
    const ok = await confirmDelete({ description: `Vai apagar o cheque "${form.cheque_number || ''}" de ${form.patient_name || cheque?.patient_name || ''}.`, linked });
    if (!ok) return;
    try { await axios.delete(`${NC}/${chequeId}`); toast.success('Cheque eliminado'); onSaved(); onClose(); }
    catch { toast.error('Erro ao eliminar'); }
  };

  const readOnly = !isAdmin;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="cheque-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {chequeId ? `Cheque ${form.cheque_number || ''}` : 'Novo Cheque Nutricionista'}
            {cheque && <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${STATUS_CLS[cheque.status]}`} data-testid="cheque-dialog-status">{STATUS_LABEL[cheque.status]}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-brand-muted mb-1 block">Paciente</Label>
            {readOnly ? <p className="text-sm text-brand-text">{cheque?.patient_name}</p> : (
              <div className="relative">
                <MagnifyingGlass size={12} className="absolute left-3 top-3 text-brand-muted" />
                <Input value={form.patient_name || patientSearch} onChange={e => { setPatientSearch(e.target.value); setForm(f => ({ ...f, patient_id: '', patient_name: '' })); }}
                  placeholder="Pesquisar paciente…" className="pl-7 rounded-xl border-brand-border" data-testid="cheque-patient-search" />
                {patients.length > 0 && !form.patient_id && (
                  <div className="absolute z-10 bg-white border border-brand-border rounded-xl mt-1 w-full shadow-lg max-h-40 overflow-y-auto">
                    {patients.map(p => (
                      <button key={p.user_id} onClick={() => { setForm(f => ({ ...f, patient_id: p.user_id, patient_name: p.name })); setPatients([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-brand-bg" data-testid={`cheque-patient-opt-${p.user_id}`}>{p.name}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs text-brand-muted mb-1 block">Nutricionista responsável</Label>
            {readOnly ? <p className="text-sm text-brand-text">{cheque?.nutri_name}</p> : (
              <Select value={form.nutri_id} onValueChange={v => setForm(f => ({ ...f, nutri_id: v }))}>
                <SelectTrigger className="rounded-xl border-brand-border" data-testid="cheque-nutri-select"><SelectValue placeholder="Escolher" /></SelectTrigger>
                <SelectContent>{staff.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>

          {form.patient_id && (
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Consulta associada {form.status === 'pedido_recebido' && '(opcional por agora)'}</Label>
              {readOnly ? <p className="text-sm text-brand-text">{form.consultation_date || 'Sem consulta associada'}</p> : (
                <Select value={form.consultation_id || ''} onValueChange={linkConsultation}>
                  <SelectTrigger className="rounded-xl border-brand-border" data-testid="cheque-consultation-select"><SelectValue placeholder="Escolher consulta" /></SelectTrigger>
                  <SelectContent>{consultations.map(c => <SelectItem key={c.id} value={c.id}>{fmtDatePt(c.date)}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-brand-muted mb-1 block">Edição</Label><Input disabled={readOnly} value={form.program_edition} onChange={e => setForm(f => ({ ...f, program_edition: e.target.value }))} className="rounded-xl border-brand-border" data-testid="cheque-edition" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Nº do cheque</Label><Input disabled={readOnly} value={form.cheque_number} onChange={e => setForm(f => ({ ...f, cheque_number: e.target.value }))} className="rounded-xl border-brand-border" data-testid="cheque-number" /></div>
          </div>
          <div><Label className="text-xs text-brand-muted mb-1 block">Data de registo na Ordem</Label><Input disabled={readOnly} type="date" value={form.registration_date} onChange={e => setForm(f => ({ ...f, registration_date: e.target.value }))} className="rounded-xl border-brand-border" data-testid="cheque-registration-date" /></div>

          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-brand-muted mb-1 block">Valor bruto €</Label><Input disabled={readOnly} inputMode="decimal" value={form.gross} onChange={e => setForm(f => ({ ...f, gross: e.target.value }))} className="rounded-xl border-brand-border" data-testid="cheque-gross" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Retenção %</Label><Input disabled={readOnly} inputMode="decimal" value={form.withholding_pct} onChange={e => setForm(f => ({ ...f, withholding_pct: e.target.value }))} className="rounded-xl border-brand-border" data-testid="cheque-withholding" /></div>
          </div>
          {!nutriIsAdmin && form.nutri_id && (
            <div><Label className="text-xs text-brand-muted mb-1 block">% Nutricionista (sobre o bruto)</Label><Input disabled={readOnly} inputMode="decimal" value={form.filipa_pct} onChange={e => setForm(f => ({ ...f, filipa_pct: e.target.value }))} className="rounded-xl border-brand-border" data-testid="cheque-filipa-pct" /></div>
          )}

          <div className="bg-brand-bg/40 rounded-xl p-3 grid grid-cols-2 gap-2 text-xs" data-testid="cheque-preview">
            <span className="text-brand-muted">Retenção: <b className="text-brand-text">{fmtEur(preview.withhold)}</b></span>
            <span className="text-brand-muted">Líquido: <b className="text-brand-text">{fmtEur(preview.net)}</b></span>
            {preview.requiresSplit && <span className="text-brand-muted">Nutricionista: <b className="text-brand-text">{fmtEur(preview.filipa)}</b></span>}
            {isAdmin && <span className="text-brand-muted">Ana: <b className="text-brand-green">{fmtEur(preview.ana)}</b></span>}
          </div>

          {cheque?.has_invoice && (
            <a href={`${NC}/${cheque.id}/fatura/pdf`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-brand-green hover:underline" data-testid="cheque-view-fatura-link">
              <FilePdf size={14} /> Ver fatura ({cheque.invoice_filename})
            </a>
          )}

          {readOnly ? null : chequeId ? (
            <>
              <button onClick={save} disabled={saving} className="w-full bg-brand-text text-white rounded-full py-2.5 text-sm font-medium" data-testid="cheque-save-btn">Guardar alterações</button>
              {cheque && <ChequeStatusActions cheque={cheque} onChanged={() => { load(); onSaved(); }} />}
              <button onClick={remove} className="w-full text-xs text-red-500 hover:underline flex items-center justify-center gap-1 pt-2" data-testid="cheque-delete-btn"><Trash size={12} /> Eliminar cheque</button>
            </>
          ) : (
            <button onClick={save} disabled={saving} className="w-full bg-brand-green text-white rounded-full py-2.5 text-sm font-medium" data-testid="cheque-create-btn">Criar cheque</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
